import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { ArrowCounterClockwise, Check, X, HandCoins } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PF, fmtEur, AMBITOS } from './pfApi';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const SWIPE_THRESHOLD = 100;

export default function PfTriage({ categories }) {
  const [queue, setQueue] = useState([]);
  const [unclassified, setUnclassified] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canUndo, setCanUndo] = useState(false);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const [q1, q2] = await Promise.all([axios.get(`${PF}/movements/untriaged`), axios.get(`${PF}/movements/unclassified-ambito`)]);
      setQueue(q1.data); setUnclassified(q2.data);
    } catch { setQueue([]); setUnclassified([]); }
    setLoading(false);
  }, []);
  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const catName = (id) => categories.find(c => c.id === id)?.name || 'Sem categoria';

  const classifyAmbito = async (movement, ambito) => {
    setUnclassified(q => q.filter(m => m.id !== movement.id));
    try { await axios.put(`${PF}/movements/${movement.id}`, { ambito }); toast.success(`Classificado como ${ambito}`); }
    catch { toast.error('Erro ao classificar'); fetchQueue(); }
  };

  const triage = async (movement, isShared) => {
    setQueue(q => q.filter(m => m.id !== movement.id));
    try {
      await axios.post(`${PF}/movements/${movement.id}/triage`, { is_shared: isShared });
      setCanUndo(true);
      toast.success(isShared ? 'Marcada como partilhada com o Duarte' : 'Marcada como não partilhada');
    } catch { toast.error('Erro ao classificar'); fetchQueue(); }
  };

  const undo = async () => {
    try { await axios.post(`${PF}/triage/undo`); toast.success('Última triagem desfeita'); setCanUndo(false); fetchQueue(); }
    catch { toast.error('Nada para desfazer'); }
  };

  const onDragEnd = (movement, info) => {
    if (info.offset.x > SWIPE_THRESHOLD) triage(movement, true);
    else if (info.offset.x < -SWIPE_THRESHOLD) triage(movement, false);
  };

  return (
    <div data-testid="pf-triage-tab">
      {unclassified.length > 0 && (
        <div className="mb-6" data-testid="pf-triage-unclassified-section">
          <p className="text-xs font-medium text-amber-700 mb-2">Por classificar (âmbito), {unclassified.length}</p>
          <div className="space-y-2">
            {unclassified.map(m => (
              <div key={m.id} className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid={`pf-unclassified-${m.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-brand-text truncate">{m.note || catName(m.category_id)}</p>
                  <p className="text-sm font-medium text-brand-text shrink-0 ml-2">{m.type === 'despesa' ? '-' : '+'}{fmtEur(m.amount)}</p>
                </div>
                <Select onValueChange={v => classifyAmbito(m, v)}>
                  <SelectTrigger className="rounded-lg border-amber-300 bg-white h-8 text-xs" data-testid={`pf-unclassified-select-${m.id}`}><SelectValue placeholder="Escolher âmbito" /></SelectTrigger>
                  <SelectContent>{AMBITOS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-brand-muted">Deslize para a <strong>direita</strong> = partilhada com o Duarte. Para a <strong>esquerda</strong> = só minha.</p>
        <button onClick={undo} disabled={!canUndo} className="text-xs text-brand-green disabled:text-brand-placeholder flex items-center gap-1 shrink-0 ml-2" data-testid="pf-triage-undo">
          <ArrowCounterClockwise size={14} /> Desfazer
        </button>
      </div>

      {loading && <p className="text-sm text-brand-muted text-center">A carregar...</p>}
      {!loading && queue.length === 0 && (
        <div className="bg-white border border-brand-border rounded-2xl p-8 text-center" data-testid="pf-triage-empty">
          <Check size={28} className="text-brand-green mx-auto mb-2" />
          <p className="text-sm text-brand-muted">Sem despesas por triar. Tudo em dia!</p>
        </div>
      )}

      <div className="relative space-y-3">
        {queue.slice(0, 6).map((m, idx) => (
          <motion.div
            key={m.id}
            drag={idx === 0 ? 'x' : false}
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.7}
            onDragEnd={(_, info) => idx === 0 && onDragEnd(m, info)}
            whileDrag={{ scale: 1.02 }}
            className={`bg-white border-2 rounded-2xl p-4 cursor-grab active:cursor-grabbing ${idx === 0 ? 'border-brand-green/40 shadow-md' : 'border-brand-border opacity-60'}`}
            data-testid={`pf-triage-card-${m.id}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-heading font-medium text-brand-text">{m.note || catName(m.category_id)}</p>
                <p className="text-[11px] text-brand-muted">{fmtDatePt(m.date)} · {catName(m.category_id)}</p>
              </div>
              <p className="text-xl font-heading font-semibold text-brand-text">{fmtEur(m.amount)}</p>
            </div>
            {idx === 0 && (
              <div className="flex gap-2">
                <button onClick={() => triage(m, false)} className="flex-1 py-2.5 rounded-xl bg-brand-bg text-brand-text text-sm font-medium flex items-center justify-center gap-1.5" data-testid={`pf-triage-btn-mine-${m.id}`}>
                  <X size={15} /> Só minha
                </button>
                <button onClick={() => triage(m, true)} className="flex-1 py-2.5 rounded-xl bg-brand-green text-white text-sm font-medium flex items-center justify-center gap-1.5" data-testid={`pf-triage-btn-shared-${m.id}`}>
                  <HandCoins size={15} /> Partilhada
                </button>
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
}
